import { useState } from 'react'
import styled from 'styled-components'
import { AccentButton } from '../Button/AccentButton'
import { TextAreaField } from '../Input'
import useFileUpload from './useFileUpload'
import useFilePreviewUrl from './useFilePreviewUrl'
import FileDropzone from './FileDropzone'
import UploadStatusCard from './UploadStatusCard'
import { formatFileSize, formatStagedMeta, getFileExtension } from './fileUpload.utils'

/** Turns the panel into a file-and-or-comment form; carries its own submit callback. */
export interface FileUploadCommentConfig {
  label?: string
  placeholder?: string
  maxLength?: number
  onUpload: (payload: { file: File | null; comment: string }) => Promise<unknown>
}

interface FileUploadPanelBaseProps {
  /** Allowed extensions, with or without a leading dot: ['.xlsx', 'csv']. */
  accept: string[]
  hint?: string
  maxSizeMb?: number
  /** Called once per successful upload — refetch the consumer's list here. */
  onUploaded?: () => void
  /** 'image' skips the row count and shows the picture instead of a format badge. */
  mode?: 'file' | 'image'
  /** Injected by AppModal. */
  closeFunc?: () => void
}

/**
 * Either the plain file flow (a file is required) or the comment flow (a file,
 * a comment, or both). Keeping them apart leaves the plain `onUpload` contract
 * untouched for existing consumers.
 */
export type FileUploadPanelProps = FileUploadPanelBaseProps &
  (
    | { onUpload: (file: File) => Promise<unknown>; comment?: undefined }
    | { onUpload?: undefined; comment: FileUploadCommentConfig }
  )

const DEFAULT_MAX_SIZE_MB = 10
const DEFAULT_COMMENT_MAX_LENGTH = 500

/**
 * Reusable upload-modal content (Hebrew, RTL) with a two-step flow:
 * drop / pick a file → staged card with row count and size meta → explicit
 * «ייבוא» → animated sending phase (min 2s) → success / server-failure card.
 * Designed as `Children` for `AppModal`; the API call is injected via `onUpload`.
 */
const FileUploadPanel = ({
  accept,
  onUpload,
  hint,
  maxSizeMb = DEFAULT_MAX_SIZE_MB,
  onUploaded,
  mode = 'file',
  comment,
  closeFunc,
}: FileUploadPanelProps) => {
  const isImage = mode === 'image'
  const [text, setText] = useState<string>('')
  const trimmedText = text.trim()

  // The comment flow owns its own callback; the plain flow keeps requiring a file.
  const runUpload = (file: File | null): Promise<unknown> => {
    if (comment) return comment.onUpload({ file, comment: trimmedText })
    if (file && onUpload) return onUpload(file)
    return Promise.reject(new Error('לא נבחר קובץ'))
  }

  const {
    file,
    status,
    rowCount,
    stagingProgress,
    errorMessage,
    isDragActive,
    canUpload,
    selectFile,
    startUpload,
    reset,
    dragHandlers,
  } = useFileUpload({
    accept,
    onUpload: runUpload,
    maxSizeMb,
    onSuccess: onUploaded,
    countRows: !isImage,
    allowFilelessUpload: Boolean(comment) && trimmedText.length > 0,
  })

  const previewUrl = useFilePreviewUrl(file, isImage)

  // Removing the file (X) keeps the typed text; starting over clears both.
  const handleResetAll = () => {
    setText('')
    reset()
  }

  if (status === 'uploading') {
    return (
      <Panel>
        <UploadStatusCard
          variant='uploading'
          fileName={file?.name ?? ''}
          fileSizeText={file ? formatFileSize(file.size) : undefined}
          previewUrl={previewUrl}
        />
      </Panel>
    )
  }

  if (status === 'success') {
    return (
      <Panel>
        <UploadStatusCard
          variant='success'
          fileName={file?.name ?? ''}
          fileSizeText={file ? formatFileSize(file.size) : undefined}
          previewUrl={previewUrl}
        />
        <Footer>
          <AccentButton variant='ghost' fullWidth={false} onClick={handleResetAll}>
            העלאת קובץ נוסף
          </AccentButton>
          <AccentButton variant='accent' fullWidth={false} onClick={closeFunc}>
            סיום
          </AccentButton>
        </Footer>
      </Panel>
    )
  }

  if (status === 'error') {
    return (
      <Panel>
        <UploadStatusCard variant='error' fileName={file?.name ?? ''} errorMessage={errorMessage} />
        <Footer>
          <AccentButton variant='ghost' fullWidth={false} onClick={closeFunc}>
            ביטול
          </AccentButton>
          <AccentButton variant='accent' fullWidth={false} onClick={startUpload}>
            ניסיון חוזר
          </AccentButton>
        </Footer>
      </Panel>
    )
  }

  // Selection step: the dropzone stays visible; the file card renders under it.
  // Dropping / picking again replaces the current file (handled by the hook).
  return (
    <Panel>
      <FileDropzone
        accept={accept}
        maxSizeMb={maxSizeMb}
        isDragActive={isDragActive}
        hasError={status === 'invalid'}
        activeExtension={file ? getFileExtension(file.name) : null}
        hint={hint}
        allowCamera={isImage}
        onFilePick={selectFile}
        dragHandlers={dragHandlers}
      />
      {status === 'invalid' && file && (
        <UploadStatusCard
          variant='invalid'
          fileName={file.name}
          errorMessage={errorMessage}
          onRemove={reset}
        />
      )}
      {status === 'staging' && file && (
        <UploadStatusCard
          variant='staging'
          fileName={file.name}
          fileSizeText={formatFileSize(file.size)}
          progress={stagingProgress}
          previewUrl={previewUrl}
        />
      )}
      {status === 'staged' && file && (
        <UploadStatusCard
          variant='staged'
          fileName={file.name}
          fileSizeText={formatStagedMeta(rowCount, file.size)}
          onRemove={reset}
          previewUrl={previewUrl}
        />
      )}
      {comment && (
        <CommentSlot>
          <TextAreaField
            name='upload-comment'
            label={comment.label ?? 'הערה'}
            placeholder={comment.placeholder}
            maxLength={comment.maxLength ?? DEFAULT_COMMENT_MAX_LENGTH}
            showCounter
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </CommentSlot>
      )}
      <Footer>
        <AccentButton variant='ghost' fullWidth={false} onClick={closeFunc}>
          ביטול
        </AccentButton>
        <AccentButton
          variant={canUpload ? 'accent' : 'ghost'}
          fullWidth={false}
          disabled={!canUpload}
          onClick={startUpload}
        >
          ייבוא
        </AccentButton>
      </Footer>
    </Panel>
  )
}

export default FileUploadPanel

const Panel = styled.div`
  direction: rtl;
  display: flex;
  flex-direction: column;
`

const CommentSlot = styled.div`
  margin-top: 0.875rem;
`

const Footer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.625rem;
  margin-top: 1.25rem;
`
